import { Component, ElementRef, OnDestroy, ViewChild, AfterViewInit, HostListener, NgZone, ChangeDetectorRef } from "@angular/core"
import { CommonModule } from "@angular/common"
import { FormsModule } from "@angular/forms"
import { MatTabsModule } from "@angular/material/tabs"
import { MatButtonModule } from "@angular/material/button"
import { MatFormFieldModule } from "@angular/material/form-field"
import { MatInputModule } from "@angular/material/input"
import { MatIconModule } from "@angular/material/icon"
import { MatChipsModule } from "@angular/material/chips"
import { MatAutocompleteModule } from "@angular/material/autocomplete"
import { MatProgressBarModule } from "@angular/material/progress-bar"
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner"
import { MatSelectModule } from "@angular/material/select"
import { MatCheckboxModule } from "@angular/material/checkbox"
import { RouterModule, Router } from "@angular/router"
import { Talent } from "../../models/talent.model"
import { TalentService } from "../../services/talent.service"
import { AuthService } from "../../services/auth.service"
import { RecordingService } from "../../services/recording.service"
import { Observable, BehaviorSubject } from "rxjs"
import { map, startWith, switchMap } from "rxjs/operators"
import { parseBlob } from "music-metadata-browser";
import * as lamejs from "@breezystack/lamejs"
import { StationStoreService } from "../../services/station-store.service"
import { StationService } from "../../services/station.service"
import { ProfileService } from "../../services/profile.service"
import { UploadService } from "../../services/upload.service"
import { SnackbarService } from "../../services/snackbar.service"
import { PermissionService } from "../../services/permission.service"
import { ProductTypeSelectorComponent } from "../../shared/product-type-selector/product-type-selector.component"
import { CoverageCitySelectComponent } from "../../shared/coverage-city-select/coverage-city-select.component"
import { StationMultiselectFieldComponent } from "../../shared/station-multiselect-field/station-multiselect-field.component"
import { PageHeaderComponent } from "../../shared/page-header/page-header.component"
import { UsCity } from "../../models/city.model"
import { Station } from "../../models/station.model"
import { getDefaultVoiceActorName } from "../../utils/default-voice-actor.util"
import { collapseRepeatedSpaces } from "../../utils/text"
import { isMp3File, MP3_FILE_ERROR } from "../../utils/audio-file"
import { MAX_SPOT_AUDIO_SECONDS, exceedsMaxSpotAudioSeconds } from "../../utils/spot-length"

@Component({
  selector: "app-audio-upload-recording",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatTabsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatChipsModule,
    MatAutocompleteModule,
    MatProgressBarModule,
    MatProgressSpinnerModule,
    MatCheckboxModule,
    MatSelectModule,
    RouterModule,
    PageHeaderComponent,
    ProductTypeSelectorComponent,
    CoverageCitySelectComponent,
    StationMultiselectFieldComponent,
  ],
  templateUrl: "./audio-upload-recording.component.html",
  styleUrl: "./audio-upload-recording.component.css",
})
export class AudioUploadRecordingComponent implements OnDestroy, AfterViewInit {
  // Upload Tab State
  uploadAudio: File | null = null
  uploadAudioUrl: string | null = null
  uploadAudioName = ""
  uploadSelectedTalent: Talent | null = null
  uploadTalentInput = ""
  uploadFileError = ""
  uploadSelectedStations: Station[] = []
  uploadDuration = 0
  isPreemptiblePromo = false
  recordingIsPreemptiblePromo = false
  selectedProductTypeIds: number[] = []

  // Immediate upload state
  uploadTmpKey: string | null = null
  uploadPreviewUrl: string | null = null
  isUploadingFile = false
  uploadFileProgress = 0

  // Recording Tab State
  recordingAudio: File | null = null
  recordingAudioUrl: string | null = null
  recordingAudioName = ""
  recordingSelectedTalent: Talent | null = null
  recordingTalentInput = ""
  isRecording = false
  showPlayer = false
  length = 60
  secondsLeft = 0
  recordingSelectedStations: Station[] = []
  recordingDuration = 0
  // Preview loading state for Record tab
  isRecordingPreviewLoading = false

  // Shared state
  uploading = false
  progress = 0
  talents: Talent[] = []
  filteredUploadTalents$: Observable<Talent[]> = new Observable()
  filteredRecordingTalents$: Observable<Talent[]> = new Observable()

  // Recording internals
  @ViewChild("waveformCanvas") waveformCanvasRef!: ElementRef<HTMLCanvasElement>
  @ViewChild("uploadAudioPlayback") uploadAudioPlaybackRef!: ElementRef<HTMLAudioElement>
  @ViewChild("recordingAudioPlayback") recordingAudioPlaybackRef!: ElementRef<HTMLAudioElement>

  private mediaRecorder: MediaRecorder | null = null
  private audioChunks: Blob[] = []
  private timer: any
  private stream: MediaStream | null = null
  private audioContext: AudioContext | null = null
  private uploadTalentInput$ = new BehaviorSubject<string>("")
  private recordingTalentInput$ = new BehaviorSubject<string>("")
  readonly accept = "audio/mpeg,audio/mp3"
  availableStations: Station[] = []
  loadingStations = false
  selectedCoverageCities: UsCity[] = []
  stationsLoadError = ""

  /** Advertiser users pick coverage cities before stations (matches order creation). */
  get usesCoverageCityFlow(): boolean {
    return this.permissionService.isAdvertiserUserOnly()
  }

  private uploadTalentPrefillDismissed = false
  private recordingTalentPrefillDismissed = false

  uploadStationSearchInput = ""
  recordingStationSearchInput = ""
  uploadStationDropdownOpen = false
  recordingStationDropdownOpen = false
  filteredUploadStations: Station[] = []
  filteredRecordingStations: Station[] = []
  uploadDropdownHover = false
  recordingDropdownHover = false

  get canSetPreemptiblePromo(): boolean {
    return this.permissionService.canSetPreemptiblePromo()
  }

  /** Only plain advertiser users go through manual station approval. */
  get requiresStationApproval(): boolean {
    return this.permissionService.isAdvertiserUserOnly()
  }

  /** Admin/owner/station roles assign product types on self-recording upload. */
  get canAssignProductTypes(): boolean {
    return !this.requiresStationApproval
  }

  constructor(
    private talentService: TalentService,
    private authService: AuthService,
    private recordingService: RecordingService,
    private router: Router,
    private stationStore: StationStoreService,
    private stationService: StationService,
    private profileService: ProfileService,
    private uploadService: UploadService,
    private snackbar: SnackbarService,
    private permissionService: PermissionService,
    private hostEl: ElementRef,
    private ngZone: NgZone,
    private cdr: ChangeDetectorRef,
  ) {
    this.loadTalents()
    this.setupTalentFilters()
    if (this.usesCoverageCityFlow) {
      this.availableStations = []
    } else {
      this.loadStations()
    }
    this.prefillDefaultVoiceActor("upload")
    this.prefillDefaultVoiceActor("recording")
  }

  ngAfterViewInit(): void {
    if (this.uploadAudioPlaybackRef?.nativeElement) {
      this.uploadAudioPlaybackRef.nativeElement.addEventListener("error", (e) => {
        console.error("[Upload] Audio playback error:", e);
      });
    }

    if (this.recordingAudioPlaybackRef?.nativeElement) {
      this.recordingAudioPlaybackRef.nativeElement.addEventListener("error", (e) => {
        console.error("[Recording] Audio playback error:", e);
      });
    }
  }

  ngOnDestroy(): void {
    this.cleanUp()
    // Clean up audio URLs to prevent memory leaks
    if (this.uploadAudioUrl) {
      URL.revokeObjectURL(this.uploadAudioUrl)
    }
    if (this.recordingAudioUrl) {
      URL.revokeObjectURL(this.recordingAudioUrl)
    }
  }

  // Fix station dropdown closing too early
  onUploadStationMouseDown(event: Event) {
    event.preventDefault();
    event.stopImmediatePropagation();
  }

  onRecordingStationMouseDown(event: Event) {
    event.preventDefault();
    event.stopImmediatePropagation();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement
    const host = this.hostEl?.nativeElement as HTMLElement
    if (!host) return
    const inAnyDropdown = !!target.closest('.multiselect-container')
    if (!inAnyDropdown) {
      this.uploadStationDropdownOpen = false
      this.recordingStationDropdownOpen = false
    }
  }

  private loadTalents() {
    this.talentService.getTalents().subscribe({
      next: (talents) => {
        this.talents = talents
      },
      error: (err) => {
        console.error("[v0] Error loading talents:", err)
      },
    })
  }

  private prefillDefaultVoiceActor(tab: "upload" | "recording"): void {
    if (tab === "upload" && this.uploadTalentPrefillDismissed) return
    if (tab === "recording" && this.recordingTalentPrefillDismissed) return

    const defaultName = getDefaultVoiceActorName(
      this.permissionService.getCurrentRole(),
      this.profileService.getCurrentUser(),
    )
    if (!defaultName) return

    this.talentService.resolveOrCreateTalentByName(defaultName).subscribe({
      next: (talent) => {
        if (tab === "upload") {
          if (this.uploadTalentPrefillDismissed) return
          this.uploadSelectedTalent = talent
          this.uploadTalentInput = talent.name
          this.uploadTalentInput$.next(talent.name)
        } else {
          if (this.recordingTalentPrefillDismissed) return
          this.recordingSelectedTalent = talent
          this.recordingTalentInput = talent.name
          this.recordingTalentInput$.next(talent.name)
        }
        if (talent.id != null && !this.talents.some((t) => t.id === talent.id)) {
          this.talents.push(talent)
        }
      },
      error: (err) => {
        console.error("[v0] Error pre-filling voice actor:", err)
      },
    })
  }

  private setupTalentFilters() {
    this.filteredUploadTalents$ = this.uploadTalentInput$.pipe(
      startWith(""),
      map((input) => {
        const searchTerm = input.toLowerCase().trim()
        if (!searchTerm) return this.talents
        return this.talents.filter((t) => t.name.toLowerCase().includes(searchTerm))
      }),
    )

    this.filteredRecordingTalents$ = this.recordingTalentInput$.pipe(
      startWith(""),
      map((input) => {
        const searchTerm = input.toLowerCase().trim()
        if (!searchTerm) return this.talents
        return this.talents.filter((t) => t.name.toLowerCase().includes(searchTerm))
      }),
    )
  }

  onUploadTalentInputChange(value: string) {
    this.uploadTalentInput = value
    this.uploadTalentInput$.next(value)
  }

  onRecordingTalentInputChange(value: string) {
    this.recordingTalentInput = value
    this.recordingTalentInput$.next(value)
  }

  isStationSelected(stations: Station[], station: Station): boolean {
    return stations.some((s) => s.id === station.id)
  }

  onUploadStationBlur() {
    setTimeout(() => {
      if (!this.uploadDropdownHover) this.uploadStationDropdownOpen = false
    }, 120)
  }

  onRecordingStationBlur() {
    setTimeout(() => {
      if (!this.recordingDropdownHover) this.recordingStationDropdownOpen = false
    }, 120)
  }

  filterUploadStations(searchTerm: string) {
    this.uploadStationSearchInput = searchTerm
    if (!searchTerm.trim()) {
      this.filteredUploadStations = this.availableStations
    } else {
      const term = searchTerm.toLowerCase()
      this.filteredUploadStations = this.availableStations.filter(
        (s) => s.name.toLowerCase().includes(term) || s.call_sign.toLowerCase().includes(term),
      )
    }
  }

  filterRecordingStations(searchTerm: string) {
    this.recordingStationSearchInput = searchTerm
    if (!searchTerm.trim()) {
      this.filteredRecordingStations = this.availableStations
    } else {
      const term = searchTerm.toLowerCase()
      this.filteredRecordingStations = this.availableStations.filter(
        (s) => s.name.toLowerCase().includes(term) || s.call_sign.toLowerCase().includes(term),
      )
    }
  }

  toggleUploadStation(station: Station) {
    const index = this.uploadSelectedStations.findIndex((s) => s.id === station.id)
    if (index > -1) {
      this.uploadSelectedStations.splice(index, 1)
    } else {
      this.uploadSelectedStations.push(station)
    }
  }

  onUploadCheckboxChange(station: Station, ev: Event) {
    const target = ev.target as HTMLInputElement
    if (target?.checked) {
      if (!this.isStationSelected(this.uploadSelectedStations, station)) {
        this.uploadSelectedStations.push(station)
      }
    } else {
      this.uploadSelectedStations = this.uploadSelectedStations.filter((s) => s.id !== station.id)
    }
  }

  toggleRecordingStation(station: Station) {
    const index = this.recordingSelectedStations.findIndex((s) => s.id === station.id)
    if (index > -1) {
      this.recordingSelectedStations.splice(index, 1)
    } else {
      this.recordingSelectedStations.push(station)
    }
  }

  onRecordingCheckboxChange(station: Station, ev: Event) {
    const target = ev.target as HTMLInputElement
    if (target?.checked) {
      if (!this.isStationSelected(this.recordingSelectedStations, station)) {
        this.recordingSelectedStations.push(station)
      }
    } else {
      this.recordingSelectedStations = this.recordingSelectedStations.filter((s) => s.id !== station.id)
    }
  }

  removeUploadStation(stationId: number) {
    this.uploadSelectedStations = this.uploadSelectedStations.filter((s) => s.id !== stationId)
  }

  removeRecordingStation(stationId: number) {
    this.recordingSelectedStations = this.recordingSelectedStations.filter((s) => s.id !== stationId)
  }

  displayTalentName(talent: Talent): string {
    return talent ? talent.name : '';
  }

  onUploadTalentSelectionChange(talent: Talent) {
    this.uploadSelectedTalent = talent
    this.uploadTalentInput = talent.name
  }

  onRecordingTalentSelectionChange(talent: Talent) {
    this.recordingSelectedTalent = talent
    this.recordingTalentInput = talent.name
  }

  removeUploadSelectedTalent(): void {
    this.uploadSelectedTalent = null
    this.uploadTalentInput = ""
    this.uploadTalentInput$.next("")
    this.uploadTalentPrefillDismissed = true
  }

  removeRecordingSelectedTalent(): void {
    this.recordingSelectedTalent = null
    this.recordingTalentInput = ""
    this.recordingTalentInput$.next("")
    this.recordingTalentPrefillDismissed = true
  }

  isUploadTalentExisting(): boolean {
    return this.talents.some((t) => t.name.toLowerCase() === this.uploadTalentInput.toLowerCase())
  }

  isRecordingTalentExisting(): boolean {
    return this.talents.some((t) => t.name.toLowerCase() === this.recordingTalentInput.toLowerCase())
  }

  async createUploadTalent(): Promise<Talent | null> {
    if (!this.uploadTalentInput.trim()) return null
    const newTalent: Talent = { name: this.uploadTalentInput.trim(), description: "Voice actor" }
    try {
      const created = await this.talentService.createTalent(newTalent).toPromise()
      if (created) {
        this.talents.push(created)
        this.uploadSelectedTalent = created
        this.uploadTalentInput = created.name
        return created
      }
    } catch (error) {
      console.error("[v0] Error creating talent:", error)
      this.snackbar.error("Failed to create talent. Please try again.")
    }
    return null
  }

  async createRecordingTalent(): Promise<Talent | null> {
    if (!this.recordingTalentInput.trim()) return null
    const newTalent: Talent = { name: this.recordingTalentInput.trim(), description: "Voice actor" }
    try {
      const created = await this.talentService.createTalent(newTalent).toPromise()
      if (created) {
        this.talents.push(created)
        this.recordingSelectedTalent = created
        this.recordingTalentInput = created.name
        return created
      }
    } catch (error) {
      console.error("[v0] Error creating talent:", error)
      this.snackbar.error("Failed to create talent. Please try again.")
    }
    return null
  }

  onUploadFileInputChange(e: Event) {
    const input = e.target as HTMLInputElement
    const file = input.files?.[0] || null
    if (file && !isMp3File(file)) {
      this.rejectUploadFile(input)
      return
    }
    this.uploadFileError = ""
    this.assignUploadFile(file)
  }

  onUploadDrop(e: DragEvent) {
    e.preventDefault()
    const file = e.dataTransfer?.files?.[0] || null
    if (file && !isMp3File(file)) {
      this.rejectUploadFile(null)
      return
    }
    this.uploadFileError = ""
    this.assignUploadFile(file)
  }

  /** Wrong type is rejected before preview, upload, or the 1-second length check. */
  private rejectUploadFile(input: HTMLInputElement | null) {
    this.clearUploadSelection()
    if (input) input.value = ""
    this.uploadFileError = MP3_FILE_ERROR
    this.snackbar.warning(MP3_FILE_ERROR)
  }

  private clearUploadSelection() {
    if (this.uploadAudioUrl) URL.revokeObjectURL(this.uploadAudioUrl)
    this.uploadAudio = null
    this.uploadAudioUrl = null
    this.uploadDuration = 0
    this.uploadTmpKey = null
    this.uploadPreviewUrl = null
    this.isUploadingFile = false
  }

  onUploadDragOver(e: DragEvent) {
    e.preventDefault()
  }

  getBlobDuration(blob: Blob): Promise<number> {
    return new Promise((resolve) => {
      const audio = document.createElement("audio");
      audio.preload = "metadata";

      audio.onloadedmetadata = () => {
        resolve(audio.duration);
        URL.revokeObjectURL(audio.src);
      };

      audio.src = URL.createObjectURL(blob);
    });
  }

  private async getAudioDuration(file: File): Promise<number> {
    try {
      const metadata = await parseBlob(file);
      return metadata.format.duration || 0;
    } catch (err) {
      console.error("Failed to read metadata:", err);
      return 0;
    }
  }


  private async assignUploadFile(file: File | null) {
    this.uploadAudio = null;
    this.uploadAudioUrl = null;
    this.uploadDuration = 0;

    if (!file) return;

    this.uploadAudio = file;
    this.uploadAudioUrl = URL.createObjectURL(file);

    if (this.uploadAudioPlaybackRef?.nativeElement) {
      const audioEl = this.uploadAudioPlaybackRef.nativeElement;
      audioEl.src = this.uploadAudioUrl;
      audioEl.load();
    }

    // Start S3 upload immediately — do not wait for metadata parsing.
    this.startImmediateUpload(file);

    // Duration in parallel (used later on submit).
    void this.getAudioDuration(file).then((duration) => {
      this.uploadDuration = duration;
    });
  }

  private startImmediateUpload(file: File) {
    this.isUploadingFile = true;
    this.uploadFileProgress = 0;
    this.uploadTmpKey = null;
    this.uploadPreviewUrl = null;

    this.uploadService.uploadFile(file, "recordings").subscribe({
      next: (result) => {
        this.uploadTmpKey = result.tmp_key;
        this.uploadPreviewUrl = result.cloudfront_preview_url;
        this.isUploadingFile = false;
        this.uploadFileProgress = 100;
        console.log("Immediate upload complete:", result);
      },
      error: (err) => {
        console.error("Immediate upload failed:", err);
        this.isUploadingFile = false;
        const message =
          err && typeof err === "object" && "message" in err
            ? String((err as { message?: unknown }).message || "")
            : ""
        this.uploadFileError = message || "Upload failed. Please try again.";
      }
    });
  }

  async startRecording() {
    try {
      this.stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      const mimeType = this.getBestMime()
      this.mediaRecorder = new MediaRecorder(this.stream, { mimeType })
      this.audioChunks = []
      this.mediaRecorder.ondataavailable = (ev: BlobEvent) => this.audioChunks.push(ev.data)
      this.mediaRecorder.onstop = async () => {
        const blob = new Blob(this.audioChunks, { type: mimeType })
        // Ensure subsequent UI updates run inside Angular zone
        await this.convertToMp3File(blob)
      }
      this.isRecording = true
      this.showPlayer = false
      this.secondsLeft = this.length
      this.mediaRecorder.start()
      this.timer = setInterval(() => {
        this.secondsLeft--
        if (this.secondsLeft <= 0) this.stopRecording()
      }, 1000)
    } catch {
      this.snackbar.warning("Microphone permission required.")
    }
  }

  stopRecording() {
    if (this.mediaRecorder && this.isRecording) {
      // Wrap UI state changes in Angular zone to trigger change detection
      this.ngZone.run(() => {
        this.isRecording = false
        // Show preview area immediately after stopping
        this.showPlayer = true
        // Loader is optional; keep flag but UI won't show spinner
        this.isRecordingPreviewLoading = true
        this.cdr.detectChanges()
      })
      this.mediaRecorder.stop()
      if (this.timer) {
        clearInterval(this.timer)
        this.timer = null
      }
      // Do NOT call cleanUp() here - it breaks MP3 conversion
      // cleanUp() will be called after conversion completes
    }
  }

  reRecord() {
    // Clear current take and immediately start a new one
    this.clearRecording()
    this.startRecording()
  }

  get timerDisplay(): string {
    const m = Math.floor(Math.max(0, this.secondsLeft) / 60)
      .toString()
      .padStart(2, "0")
    const s = Math.max(0, this.secondsLeft) % 60
    return `${m}:${s.toString().padStart(2, "0")}`
  }

  clearRecording() {
    this.recordingAudio = null
    this.showPlayer = false
    this.secondsLeft = 0
    this.recordingDuration = 0 // Reset duration

    if (this.timer) {
      clearInterval(this.timer)
      this.timer = null
    }

    if (this.recordingAudioUrl) {
      URL.revokeObjectURL(this.recordingAudioUrl)
      this.recordingAudioUrl = null
    }

    if (this.recordingAudioPlaybackRef?.nativeElement) {
      this.recordingAudioPlaybackRef.nativeElement.src = ""
    }
  }

  private getBestMime(): string {
    const types = ["audio/webm;codecs=pcm", "audio/webm;codecs=opus", "audio/webm", "audio/ogg;codecs=opus"]
    for (const t of types) if (MediaRecorder.isTypeSupported(t)) return t
    return "audio/webm"
  }

  private async convertToMp3File(audioBlob: Blob) {
    try {
      const ctx = new (window.AudioContext || (window as any).webkitAudioContext)()
      const buf = await ctx.decodeAudioData(await audioBlob.arrayBuffer())
      const left = buf.getChannelData(0)
      const right = buf.numberOfChannels > 1 ? buf.getChannelData(1) : left
      const l16 = this.floatTo16(left)
      const r16 = this.floatTo16(right)
      const enc = new lamejs.Mp3Encoder(buf.numberOfChannels, buf.sampleRate, 128)
      const block = 1152
      const out: Uint8Array[] = []
      for (let i = 0; i < l16.length; i += block) {
        const li = l16.subarray(i, i + block)
        const ri = r16.subarray(i, i + block)
        const mp3buf = buf.numberOfChannels === 1 ? enc.encodeBuffer(li) : enc.encodeBuffer(li, ri)
        if (mp3buf.length) out.push(mp3buf)
      }
      const last = enc.flush()
      if (last.length) out.push(last)
      const mp3Blob = new Blob(out, { type: "audio/mpeg" })
      const ts = new Date().toISOString().replace(/[:.]/g, "-")
      this.recordingAudio = new File([mp3Blob], `recording-${ts}.mp3`, { type: "audio/mpeg" })

      if (this.recordingAudioUrl) {
        URL.revokeObjectURL(this.recordingAudioUrl)
      }
      this.recordingAudioUrl = URL.createObjectURL(this.recordingAudio)

      // Extract duration using parseBlob (same as upload tab for consistency)
      try {
        const metadata = await parseBlob(this.recordingAudio);
        this.recordingDuration = metadata.format.duration || 0;
        console.log("[Recording] Duration from parseBlob:", this.recordingDuration);
      } catch (metadataError) {
        console.warn("[Recording] parseBlob failed, using audio element fallback:", metadataError);
        // Fallback: use audio element metadata
        this.recordingDuration = await this.getBlobDuration(this.recordingAudio);
        console.log("[Recording] Duration from audio element:", this.recordingDuration);
      }

      // Set up audio playback
      if (this.recordingAudioPlaybackRef?.nativeElement) {
        this.ngZone.run(() => {
          const audioEl = this.recordingAudioPlaybackRef.nativeElement;
          // Ensure preview appears without extra click: set src and load
          audioEl.src = this.recordingAudioUrl as string;
          audioEl.onloadedmetadata = () => {
            this.isRecordingPreviewLoading = false;
            this.cdr.detectChanges();
          };
          audioEl.load();
          // Also proactively mark the loader as done since we have a playable URL
          this.isRecordingPreviewLoading = false;
          this.cdr.detectChanges();
        })
      } else {
        // If the element isn't in the DOM yet (lazy render), schedule update
        this.ngZone.run(() => {
          this.isRecordingPreviewLoading = false;
          this.cdr.detectChanges();
        })
      }

      // Ensure change detection picks up updates in next tick as well
      setTimeout(() => {
        this.ngZone.run(() => {
          this.isRecordingPreviewLoading = false;
          this.cdr.detectChanges();
        })
      }, 0);

      await this.processAudioAndDrawWaveform(this.recordingAudio)

      // Cleanup after successful MP3 conversion
      await this.cleanUp()

    } catch (error) {
      console.error("[v0] MP3 conversion error:", error)
      const ts = new Date().toISOString().replace(/[:.]/g, "-")
      // Fallback to WAV as requested
      this.recordingAudio = new File([audioBlob], `recording-${ts}.wav`, { type: "audio/wav" })

      if (this.recordingAudioUrl) {
        URL.revokeObjectURL(this.recordingAudioUrl)
      }
      this.recordingAudioUrl = URL.createObjectURL(this.recordingAudio)

      // Extract duration for WAV fallback
      try {
        const metadata = await parseBlob(this.recordingAudio);
        this.recordingDuration = metadata.format.duration || 0;
        console.log("[Recording] WAV Duration from parseBlob:", this.recordingDuration);
      } catch (metadataError) {
        console.warn("[Recording] WAV parseBlob failed, using audio element fallback:", metadataError);
        this.recordingDuration = await this.getBlobDuration(this.recordingAudio);
        console.log("[Recording] WAV Duration from audio element:", this.recordingDuration);
      }

      if (this.recordingAudioPlaybackRef?.nativeElement) {
        this.ngZone.run(() => {
          const audioEl = this.recordingAudioPlaybackRef.nativeElement;
          audioEl.src = this.recordingAudioUrl as string;
          audioEl.onloadedmetadata = () => {
            this.isRecordingPreviewLoading = false;
            this.cdr.detectChanges();
          };
          audioEl.load();
          this.isRecordingPreviewLoading = false;
          this.cdr.detectChanges();
        })
      } else {
        this.ngZone.run(() => {
          this.isRecordingPreviewLoading = false;
          this.cdr.detectChanges();
        })
      }

      // As above, ensure the loader closes automatically regardless of DOM event timing.
      setTimeout(() => {
        this.ngZone.run(() => {
          this.isRecordingPreviewLoading = false;
          this.cdr.detectChanges();
        })
      }, 0);

      // Cleanup after fallback conversion
      await this.cleanUp()
    }
  }

  // Called when the recording preview audio element has loaded metadata
  onRecordingPreviewMetadataLoaded() {
    this.isRecordingPreviewLoading = false
  }

  private floatTo16(input: Float32Array): Int16Array {
    const out = new Int16Array(input.length)
    for (let i = 0; i < input.length; i++) {
      const s = Math.max(-1, Math.min(1, input[i]))
      out[i] = s < 0 ? s * 0x8000 : s * 0x7fff
    }
    return out
  }

  private async processAudioAndDrawWaveform(audioBlob: Blob): Promise<void> {
    // Use a local AudioContext to avoid races with global cleanup
    const localCtx = new (window.AudioContext || (window as any).webkitAudioContext)()
    try {
      // Convert Blob to ArrayBuffer with fallback
      let arrayBuffer: ArrayBuffer;
      try {
        arrayBuffer = await audioBlob.arrayBuffer()
      } catch (e) {
        arrayBuffer = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as ArrayBuffer);
          reader.onerror = reject;
          reader.readAsArrayBuffer(audioBlob);
        });
      }

      // Decode the audio data
      const audioBuffer = await localCtx.decodeAudioData(arrayBuffer)

      // Get channel data
      const channelData = audioBuffer.getChannelData(0)

      // Draw the waveform if canvas exists
      this.drawWaveform(channelData)
    } catch (error) {
      console.error("Error processing audio:", error)
    } finally {
      try { await localCtx.close() } catch { }
    }
  }

  private drawWaveform(audioData: Float32Array): void {
    if (!this.waveformCanvasRef?.nativeElement) return

    const canvas = this.waveformCanvasRef.nativeElement
    const ctx = canvas.getContext("2d")
    if (!ctx) return

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height)

    const width = canvas.width
    const height = canvas.height
    const centerY = height / 2

    // For performance, reduce number of points to draw by sampling
    const step = Math.max(1, Math.ceil(audioData.length / width))

    // Set waveform style
    ctx.fillStyle = "#4285f4"

    // Draw waveform correctly - top and bottom separately
    for (let i = 0; i < width; i++) {
      const index = Math.min(i * step, audioData.length - 1)

      // For better visualization, use peak values in each segment
      let maxAmp = 0
      for (let j = 0; j < step && index + j < audioData.length; j++) {
        const amp = Math.abs(audioData[index + j])
        if (amp > maxAmp) maxAmp = amp
      }

      const barHeight = maxAmp * centerY * 0.95

      // Draw vertical bar from center
      ctx.fillRect(i, centerY - barHeight, 1, barHeight * 2)
    }

    // Draw center line
    ctx.strokeStyle = "rgba(0, 0, 0, 0.2)"
    ctx.lineWidth = 1
    ctx.beginPath()
    ctx.moveTo(0, centerY)
    ctx.lineTo(width, centerY)
    ctx.stroke()
  }

  private async cleanUp(): Promise<void> {
    if (this.timer) {
      clearInterval(this.timer)
      this.timer = null
    }

    if (this.stream) {
      this.stream.getTracks().forEach((track) => track.stop())
      this.stream = null
    }

    // Do NOT call this.mediaRecorder.stop() here
    this.mediaRecorder = null

    if (this.audioContext) {
      try {
        await this.audioContext.close()
      } catch (e) {
        console.error("Error closing AudioContext:", e)
      }
      this.audioContext = null
    }
  }

  private loadStations() {
    this.loadingStations = true
    this.stationStore.watchStations().subscribe({
      next: (stations) => {
        this.availableStations = stations
        this.filteredUploadStations = this.availableStations
        this.filteredRecordingStations = this.availableStations
        this.loadingStations = false
      },
      error: (err) => {
        console.error("[v0] Error loading stations:", err)
        this.loadingStations = false
      },
    })
  }

  onCoverageCitiesChange(cities: UsCity[]): void {
    if (!this.usesCoverageCityFlow) return

    this.selectedCoverageCities = cities
    this.uploadSelectedStations = []
    this.recordingSelectedStations = []
    this.availableStations = []
    this.stationsLoadError = ""

    if (cities.length === 0) {
      return
    }

    this.loadStationsByCities(cities.map((city) => city.name))
  }

  private loadStationsByCities(cityNames: string[]): void {
    if (!cityNames.length) {
      this.availableStations = []
      return
    }

    this.loadingStations = true
    this.stationsLoadError = ""
    this.stationService.getAllStationsByCities(cityNames).subscribe({
      next: (response) => {
        this.availableStations = (response.stations || []).sort((a, b) =>
          a.call_sign.localeCompare(b.call_sign),
        )
        this.loadingStations = false
      },
      error: (err) => {
        console.error("Error loading stations by city:", err)
        this.stationsLoadError = "Failed to load stations for the selected cities"
        this.snackbar.error("Failed to load stations for selected cities", 3000)
        this.loadingStations = false
      },
    })
  }

  normalizeUploadAudioName(): void {
    this.uploadAudioName = collapseRepeatedSpaces(this.uploadAudioName)
  }

  normalizeRecordingAudioName(): void {
    this.recordingAudioName = collapseRepeatedSpaces(this.recordingAudioName)
  }

  async submitUpload() {
    this.normalizeUploadAudioName()
    if (!this.uploadAudioName?.trim()) {
      this.snackbar.info("Please provide an audio name.")
      return
    }
    if (!this.uploadAudio || this.uploadAudio.size === 0) {
      this.snackbar.warning("No audio file selected.")
      return
    }
    if (!isMp3File(this.uploadAudio)) {
      this.uploadFileError = MP3_FILE_ERROR
      this.snackbar.warning(MP3_FILE_ERROR)
      return
    }
    if (!this.uploadSelectedTalent && !this.uploadTalentInput.trim()) {
      this.snackbar.warning("Please select a talent or add a new one.")
      return
    }
    if (this.uploadSelectedStations.length === 0) {
      this.snackbar.warning("Please select at least one station for this recording.")
      return
    }

    // Create talent if needed
    if (this.uploadTalentInput.trim() && !this.uploadSelectedTalent) {
      if (!this.isUploadTalentExisting()) {
        const created = await this.createUploadTalent()
        if (!created) {
          this.snackbar.error("Failed to create talent.")
          return
        }
        this.uploadSelectedTalent = created
      }
    }

    if (!this.uploadSelectedTalent) {
      this.snackbar.error("Failed to select or create talent.")
      return
    }

    this.uploading = true
    this.progress = 10

    const cachedUser = this.profileService.getCurrentUser()

    if (!cachedUser) {
      this.snackbar.error("User profile not loaded. Please log in again.")
      this.uploading = false
      return
    }

    const uploaderid = cachedUser.userid
    if (!uploaderid) {
      this.snackbar.error("Unable to get your user ID. Please refresh and try again.")
      this.uploading = false
      return
    }

    const talentid = this.uploadSelectedTalent?.id ?? 0

    // Use stored duration
    let duration = this.uploadDuration;
    if (!isFinite(duration)) duration = 0;
    console.log("Duration:", duration);
    if (duration < 1) {
      this.snackbar.warning("Audio is too short (minimum 1 second).");
      this.uploading = false;
      return;
    }
    // get_stationrates.derive_spot_length_from_audio rejects anything over 65s, so a longer
    // recording could never be matched to a rate or scheduled.
    if (exceedsMaxSpotAudioSeconds(duration)) {
      this.snackbar.warning(`Audio is too long (maximum ${MAX_SPOT_AUDIO_SECONDS} seconds).`);
      this.uploading = false;
      return;
    }

    // Proceed with upload
    // Proceed with upload
    // Check if immediate upload is finished
    if (this.isUploadingFile) {
      this.snackbar.info("Please wait for the file upload to complete.");
      return;
    }

    if (!this.uploadTmpKey) {
      // If no key, maybe upload failed or wasn't started. Retry?
      // For now, alert user.
      this.snackbar.error("File upload failed or not started. Please re-select the file.");
      return;
    }

    this.uploading = true;
    this.progress = 50; // Assume upload is done, just API call left

    this.recordingService.create({
      name: this.uploadAudioName.trim(),
      uploaderid,
      status: 0,
      talentid,
      producttype: 1,
      audio_tmp_key: this.uploadTmpKey, // Use the pre-uploaded key
      audiolength: duration,
      submitted_stations: this.uploadSelectedStations.map((s) => s.id),
      ...(this.canAssignProductTypes ? { product_type_ids: this.selectedProductTypeIds } : {}),
      is_preemptible_promo: this.canSetPreemptiblePromo && this.isPreemptiblePromo,
      ...this.recordingCreateNotificationFlags(),
    }).subscribe({
      next: (res) => {
        this.progress = 100
        this.uploading = false
        if (this.canAssignProductTypes) {
          this.selectedProductTypeIds = []
        }
        this.snackbar.success(this.recordingCreatedMessage(res))
        this.router.navigate(["/recordings"])
      },
      error: (e) => {
        this.uploading = false
        console.error("Upload error:", e)
        this.snackbar.error(e?.error?.error || "Upload failed. Please try again.")
      },
    })
  }

  async submitRecording() {
    this.normalizeRecordingAudioName()
    if (!this.recordingAudio || this.recordingAudio.size === 0) {
      this.snackbar.warning("No recording available.")
      return
    }
    if (!this.recordingSelectedTalent && !this.recordingTalentInput.trim()) {
      this.snackbar.warning("Please select a talent or add a new one.")
      return
    }
    if (this.recordingSelectedStations.length === 0) {
      this.snackbar.warning("Please select at least one station for this recording.")
      return
    }

    if (this.recordingTalentInput.trim() && !this.recordingSelectedTalent) {
      if (!this.isRecordingTalentExisting()) {
        const created = await this.createRecordingTalent()
        if (!created) {
          this.snackbar.error("Failed to create talent.")
          return
        }
        this.recordingSelectedTalent = created
      }
    }

    if (!this.recordingSelectedTalent) {
      this.snackbar.error("Failed to select or create talent.")
      return
    }

    this.uploading = true
    this.progress = 10

    const cachedUser = this.profileService.getCurrentUser()

    if (!cachedUser) {
      this.snackbar.error("User profile not loaded. Please log in again.")
      this.uploading = false
      return
    }

    const uploaderid = cachedUser.userid
    if (!uploaderid) {
      this.snackbar.error("Unable to get your user ID. Please refresh and try again.")
      this.uploading = false
      return
    }

    const talentid = this.recordingSelectedTalent?.id ?? 0

    // Use stored duration
    let duration = this.recordingDuration;
    if (!isFinite(duration)) duration = 0;

    if (duration < 1) {
      this.snackbar.warning("Recording is too short (minimum 1 second).");
      this.uploading = false;
      return;
    }
    if (exceedsMaxSpotAudioSeconds(duration)) {
      this.snackbar.warning(`Recording is too long (maximum ${MAX_SPOT_AUDIO_SECONDS} seconds).`);
      this.uploading = false;
      return;
    }

    // Proceed with recording upload
    this.uploadService.uploadFile(this.recordingAudio!, "recordings").pipe(
      switchMap((result) => {
        const autoName = this.recordingAudioName?.trim() || `Recording ${new Date().toLocaleString()}`
        return this.recordingService.create({
          name: autoName,
          uploaderid,
          status: 0,
          talentid,
          producttype: 1,
          audio_tmp_key: result.tmp_key,
          audiolength: duration,
          submitted_stations: this.recordingSelectedStations.map((s) => s.id),
          ...(this.canAssignProductTypes ? { product_type_ids: this.selectedProductTypeIds } : {}),
          is_preemptible_promo: this.canSetPreemptiblePromo && this.recordingIsPreemptiblePromo,
          ...this.recordingCreateNotificationFlags(),
        })
      })
    ).subscribe({
      next: (res) => {
        this.progress = 100
        this.uploading = false
        if (this.canAssignProductTypes) {
          this.selectedProductTypeIds = []
        }
        this.snackbar.success(this.recordingCreatedMessage(res))
        this.router.navigate(["/recordings"])
      },
      error: (e) => {
        this.uploading = false
        console.error("Upload error:", e)
        const msg = e?.error?.error || "Upload failed. Please try again."
        this.snackbar.error(msg)
      },
    })
  }

  onUploadStationChange(station: Station, event: Event) {
    const checked = (event.target as HTMLInputElement).checked
    if (checked) {
      if (!this.uploadSelectedStations.some((s: Station) => s.id === station.id)) {
        this.uploadSelectedStations.push(station)
      }
    } else {
      this.uploadSelectedStations = this.uploadSelectedStations.filter((s: Station) => s.id !== station.id)
    }
  }

  /**
   * Staff uploads should not notify advertiser accounts; customer (`user`) uploads keep default notify behavior.
   * Backend must honor `suppress_customer_notifications` on POST /audioRecordings.
   */
  private recordingCreateNotificationFlags(): { suppress_customer_notifications?: boolean } {
    const role = this.permissionService.getCurrentRole()
    if (role === "user") return {}
    return { suppress_customer_notifications: true }
  }

  private recordingCreatedMessage(res?: { message?: string; autoApproved?: boolean }): string {
    if (res?.message) return res.message
    if (res?.autoApproved || !this.requiresStationApproval) {
      return "Recording created and approved."
    }
    return "Recording created. Pending station approval."
  }

  onRecordingStationChange(station: Station, event: Event) {
    const checked = (event.target as HTMLInputElement).checked
    if (checked) {
      if (!this.recordingSelectedStations.some((s: Station) => s.id === station.id)) {
        this.recordingSelectedStations.push(station)
      }
    } else {
      this.recordingSelectedStations = this.recordingSelectedStations.filter((s: Station) => s.id !== station.id)
    }
  }
}
